import AppChip from '@/ui/chip/AppChip';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const STORAGE_KEY = 'ai-customization-roadmap-expanded';

const getInitialExpandedState = (): boolean => {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored === null ? true : stored === 'true';
};

enum RoadmapStatus {
  AVAILABLE = 'available',
  COMING_2026 = 'coming-2026',
  TBA = 'tba',
}

type RoadmapCardData = {
  titleKey: string;
  descriptionKey: string;
  icon: string;
  status: RoadmapStatus;
};

const ROADMAP_CARDS: RoadmapCardData[] = [
  {
    titleKey: 'course.customization.roadmap.orgContext.title',
    descriptionKey: 'course.customization.roadmap.orgContext.description',
    icon: 'pi pi-building',
    status: RoadmapStatus.AVAILABLE,
  },
  {
    titleKey: 'course.customization.roadmap.manualEditing.title',
    descriptionKey: 'course.customization.roadmap.manualEditing.description',
    icon: 'pi pi-file-edit',
    status: RoadmapStatus.AVAILABLE,
  },
  {
    titleKey: 'course.customization.roadmap.aiTranslation.title',
    descriptionKey: 'course.customization.roadmap.aiTranslation.description',
    icon: 'pi pi-language',
    status: RoadmapStatus.AVAILABLE,
  },
  {
    titleKey: 'course.customization.roadmap.pointsOfContact.title',
    descriptionKey: 'course.customization.roadmap.pointsOfContact.description',
    icon: 'pi pi-user',
    status: RoadmapStatus.COMING_2026,
  },
  {
    titleKey: 'course.customization.roadmap.guidelines.title',
    descriptionKey: 'course.customization.roadmap.guidelines.description',
    icon: 'pi pi-file',
    status: RoadmapStatus.COMING_2026,
  },
  {
    titleKey: 'course.customization.roadmap.roleSpecific.title',
    descriptionKey: 'course.customization.roadmap.roleSpecific.description',
    icon: 'pi pi-users',
    status: RoadmapStatus.COMING_2026,
  },
  {
    titleKey: 'course.customization.roadmap.videoCustomisation.title',
    descriptionKey:
      'course.customization.roadmap.videoCustomisation.description',
    icon: 'pi pi-video',
    status: RoadmapStatus.TBA,
  },
];

const STATUS_CONFIG: Record<
  RoadmapStatus,
  {
    labelKey: string;
    chipType: string;
    pillIcon: string;
  }
> = {
  [RoadmapStatus.AVAILABLE]: {
    labelKey: 'course.customization.roadmap.status.available',
    chipType: 'primary',
    pillIcon: 'pi pi-check',
  },
  [RoadmapStatus.COMING_2026]: {
    labelKey: 'course.customization.roadmap.status.coming2026',
    chipType: 'yellow',
    pillIcon: 'pi pi-clock',
  },
  [RoadmapStatus.TBA]: {
    labelKey: 'course.customization.roadmap.status.tba',
    chipType: 'platinum',
    pillIcon: 'pi pi-ellipsis-h',
  },
};

const SectionContainer = styled.div`
  background: var(--white-main);
  border-radius: var(--semi-medium-border-radius);
  padding: var(--medium-big-padding);
  width: 100%;
  max-width: 1000px;
  margin: var(--default-padding) auto 0;
`;

const SectionHeader = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
`;

const SectionTitle = styled.span`
  font-weight: 600;
  font-size: var(--medium-font-size);
  line-height: 1.4;
  color: var(--black-main);
`;

const ChevronIcon = styled.i<{ expanded: boolean }>`
  font-size: var(--medium-font-size);
  color: var(--black-main);
  transition: transform 0.2s;
  transform: ${({ expanded }) => (expanded ? 'rotate(180deg)' : 'rotate(0)')};
`;

const CardsContainer = styled.div<{ visible: boolean }>`
  display: ${({ visible }) => (visible ? 'flex' : 'none')};
  flex-direction: column;
  gap: var(--medium-padding);
  margin-top: var(--default-padding-small);
`;

const CardRow = styled.div`
  display: flex;
  gap: var(--medium-padding);
  width: 100%;
`;

const Card = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--semi-medium-padding);
  justify-content: space-between;
  background: var(--white-main);
  border: 1px solid var(--gray-border-lighter);
  border-radius: var(--xsmall-border-radius);
  padding: var(--medium-padding);
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--xsmall-padding);
`;

const CardIcon = styled.i`
  font-size: var(--heading-medium-font-size);
  color: var(--black-main);
  -webkit-text-stroke: 1px var(--white-main);
`;

const CardTextGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--xxsmall-padding);
`;

const CardTitle = styled.span`
  font-weight: 600;
  font-size: var(--small-font-size);
  line-height: 1.4;
  color: var(--black-main);
`;

const CardDescription = styled.span`
  font-weight: 400;
  font-size: var(--xsmall-font-size);
  line-height: 1.4;
  color: var(--black-main);
`;

export const FeatureRoadmapSection: React.FC = () => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(getInitialExpandedState);

  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => {
      const next = !prev;
      localStorage.setItem(STORAGE_KEY, String(next));
      return next;
    });
  }, []);

  const topRow = ROADMAP_CARDS.slice(0, 3);
  const bottomRow = ROADMAP_CARDS.slice(3);

  return (
    <SectionContainer>
      <SectionHeader type="button" onClick={toggleExpanded}>
        <SectionTitle>{t('course.customization.roadmap.title')}</SectionTitle>
        <ChevronIcon className="pi pi-chevron-up" expanded={expanded} />
      </SectionHeader>

      <CardsContainer visible={expanded}>
        <CardRow>
          {topRow.map((card) => {
            const statusConfig = STATUS_CONFIG[card.status];
            return (
              <Card key={card.titleKey}>
                <CardContent>
                  <CardIcon className={card.icon} />
                  <CardTextGroup>
                    <CardTitle>{t(card.titleKey)}</CardTitle>
                    <CardDescription>{t(card.descriptionKey)}</CardDescription>
                  </CardTextGroup>
                </CardContent>
                <AppChip
                  label={t(statusConfig.labelKey)}
                  type={statusConfig.chipType}
                  size="xs-auto"
                  icon={
                    <i
                      className={statusConfig.pillIcon}
                      style={{ marginRight: 'var(--xxsmall-padding)' }}
                    />
                  }
                  style={{ alignSelf: 'flex-start' }}
                />
              </Card>
            );
          })}
        </CardRow>
        <CardRow>
          {bottomRow.map((card) => {
            const statusConfig = STATUS_CONFIG[card.status];
            return (
              <Card key={card.titleKey}>
                <CardContent>
                  <CardIcon className={card.icon} />
                  <CardTextGroup>
                    <CardTitle>{t(card.titleKey)}</CardTitle>
                    <CardDescription>{t(card.descriptionKey)}</CardDescription>
                  </CardTextGroup>
                </CardContent>
                <AppChip
                  label={t(statusConfig.labelKey)}
                  type={statusConfig.chipType}
                  size="xs-auto"
                  icon={
                    <i
                      className={statusConfig.pillIcon}
                      style={{ marginRight: 'var(--xxsmall-padding)' }}
                    />
                  }
                  style={{ alignSelf: 'flex-start' }}
                />
              </Card>
            );
          })}
        </CardRow>
      </CardsContainer>
    </SectionContainer>
  );
};
